/**
 * THE BUTTON THAT GETS A LOST SEASON BACK. D-EZ-66.
 *
 * Howard, 19 Aug 2026, on finding season 1 missing from his cards:
 * *"Its lost data?"*
 *
 * It was not, and this is where he finds that out. The panel appears only when
 * the league can actually see a hole — a live season 3 with nothing behind it —
 * and it says which years are missing BEFORE it offers to do anything, because
 * "rebuild" with no object is a button nobody should press.
 *
 * ⚠ IT SHOWS ITS WORKING WHETHER IT SUCCEEDS OR NOT. `rebuildPriorSeasons`
 * returns every comparison it made, and all of them are printed. A recovery the
 * player has to take on trust is indistinguishable from an invention, and the
 * whole difference between the two is a check somebody can read.
 *
 * ⚠ IT NEVER SAVES. It puts the recovered seasons on the workbench and says to
 * press Save. Writing to the slot from here would mean a rebuild that went wrong
 * in a way the checks did not catch could overwrite the one file that holds the
 * carry the rebuild was checked against — the thing that makes a second attempt
 * possible.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  rebuildPriorSeasons,
  type RebuildCheck,
  type RebuildCarry,
  type RebuildOutcome,
} from '../lib/seasonRebuild.ts';
import type { CareerSeason } from '../lib/careerRunner.ts';
import type { NamePool } from '../lib/nameStore.ts';

export interface RebuildSeasonsProps {
  /** The season on the workbench — the one with a hole behind it. */
  liveSeasonNumber: number;
  liveSeed: number;
  /** The roster, garage and counter that season began from. */
  carry: RebuildCarry;
  /** Season numbers already on the shelf, so the panel names what is missing. */
  have: readonly number[];
  pool: NamePool | null;
  onRebuilt: (seasons: CareerSeason[]) => void;
}

/** The seasons a league of this many years should have behind it, and does not. */
export function missingSeasons(liveSeasonNumber: number, have: readonly number[]): number[] {
  const held = new Set(have);
  const out: number[] = [];
  for (let n = 1; n < liveSeasonNumber; n++) if (!held.has(n)) out.push(n);
  return out;
}

/** "season 1", "seasons 1 and 2", "seasons 1, 2 and 3". */
export function listSeasons(numbers: readonly number[]): string {
  const n = numbers.map(String);
  if (n.length === 0) return 'no seasons';
  if (n.length === 1) return `season ${n[0]}`;
  return `seasons ${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}

/**
 * What the panel shows once a rebuild has finished, and whether the seasons go
 * to the shell.
 *
 * ⚠ PURE, AND SEPARATE FROM THE COMPONENT ON PURPOSE. This repository renders
 * components to static markup in tests and has no DOM driver, so a decision left
 * inside a click handler is a decision nothing can check. The two that matter
 * are both here: a refusal must NOT hand seasons over, and "nothing has been
 * saved yet" must only appear when there is something on the workbench to save.
 */
export interface PanelView {
  line: string;
  good: boolean;
  checks: RebuildCheck[] | null;
  /** True only when verified seasons should be pushed to the shell. */
  handOver: boolean;
  seasons: CareerSeason[];
}

export function viewFor(result: RebuildOutcome | Error): PanelView {
  if (result instanceof Error) {
    return {
      line: `The rebuild could not finish and nothing was changed — ${result.message}.`,
      good: false,
      checks: null,
      handOver: false,
      seasons: [],
    };
  }
  return {
    line: result.line,
    good: result.ok,
    checks: result.checks,
    handOver: result.ok && result.seasons.length > 0,
    seasons: result.ok ? result.seasons : [],
  };
}

interface Progress {
  stage: string;
  done: number;
  total: number;
}

export default function RebuildSeasons({
  liveSeasonNumber,
  liveSeed,
  carry,
  have,
  pool,
  onRebuilt,
}: RebuildSeasonsProps) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [checks, setChecks] = useState<RebuildCheck[] | null>(null);
  const [line, setLine] = useState<string | null>(null);
  const [good, setGood] = useState(false);
  const alive = useRef(true);
  /**
   * ⚠ A REBUILD OUTLIVES THE PANEL. It is seconds of engine work per season and
   * the player can close the book while it runs. Without this the first `setState`
   * after an unmount warns, and — worse — `onRebuilt` would push a whole league
   * onto a shell that has moved on.
   */
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const missing = missingSeasons(liveSeasonNumber, have);

  const run = useCallback(async () => {
    setBusy(true);
    setChecks(null);
    setLine(null);
    setGood(false);
    try {
      let view: PanelView;
      try {
        view = viewFor(
          await rebuildPriorSeasons({
            liveSeasonNumber,
            liveSeed,
            carry,
            pool,
            onProgress: (stage, done, total) => {
              if (alive.current) setProgress({ stage, done, total });
            },
          }),
        );
      } catch (e) {
        view = viewFor(e instanceof Error ? e : new Error(String(e)));
      }
      if (!alive.current) return;
      setChecks(view.checks);
      setLine(view.line);
      setGood(view.good);
      if (view.handOver) onRebuilt(view.seasons);
    } finally {
      if (alive.current) {
        setBusy(false);
        setProgress(null);
      }
    }
  }, [liveSeasonNumber, liveSeed, carry, pool, onRebuilt]);

  if (missing.length === 0) return null;

  return (
    <section className="rbld" aria-labelledby="rbld-h">
      <p className="kicker">Missing from this league</p>
      <h3 id="rbld-h" className="rbld__h">
        {listSeasons(missing).replace(/^s/, 'S')} {missing.length === 1 ? 'is' : 'are'} not in this
        file
      </h3>
      <p className="rbld__why">
        {/*
          ⚠ THE CAUSE, NOT THE SYMPTOM. This league was saved before seasons were
          kept with the file, so the years behind the live one were never
          written. Saying "no data" would read as a loss; saying which change
          left the hole is the difference between a limitation and a bug.
        */}
        This league was saved before finished seasons were kept with the file, so the years behind{' '}
        season {liveSeasonNumber} were never written down. They can be got back: the game is seeded,
        so re-running the same seeds plays the same races, and the roster this season began from is
        what proves the rebuild is the same league and not a new one.
      </p>

      <button type="button" className="btn btn--hot" onClick={run} disabled={busy}>
        {busy ? 'Rebuilding…' : `Rebuild ${listSeasons(missing)}`}
      </button>

      {progress && (
        <p className="rbld__prog" role="status">
          {progress.stage}
          {progress.total > 0 && (
            <span className="rbld__bar" aria-hidden="true">
              <span style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
            </span>
          )}
        </p>
      )}

      {line && (
        <p className={`rbld__line${good ? ' is-good' : ' is-bad'}`} role="status">
          {line}
        </p>
      )}

      {checks && (
        <ul className="rbld__checks">
          {checks.map((c) => (
            <li key={c.what} className={c.ok ? 'is-ok' : 'is-no'}>
              <span className="rbld__what">{c.what}</span>
              <span className="rbld__detail">{c.detail}</span>
            </li>
          ))}
        </ul>
      )}

      {good && (
        <p className="rbld__next">
          {/*
            ⚠ NOTHING HAS BEEN SAVED, AND IT SAYS SO. The seasons are on the
            workbench; the slot still holds what it held. A player who closes the
            tab now has lost only the minutes, and one who presses Save keeps it.
          */}
          They are on the workbench now — the record book, the winter and the career rows on every
          driver card. <strong>Nothing has been saved yet.</strong> Press Save to keep them.
        </p>
      )}
    </section>
  );
}
